import { useCallback, useEffect, useRef, useState } from 'react';
import { GridHeaderSelectionChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { CpsFilterRequest, DOC_ID_FIELD } from '@cs-cms/types';
import { useDocumentsContext, useSearchContext } from '@cs-cms/state-management';
import { areQueryFiltersEqual } from '../helpers/cpsGridHelpers';
import { FilterActionsHandle } from '../toolbar/filterActions';
import { resolveClickedCellMetadata } from '../toolbar/filterActions/resolveClickedCellMetadata';
import { CpsGridDocument } from '../cpsGridTypes';

interface AppliedFiltersSnapshot {
  viewKey: string;
  filters: CpsFilterRequest[];
  searchText: string;
  actionsFilter: number[];
}

interface PageSnapshot {
  viewKey: string;
  skip: number;
  size: number;
}

const areActionsFiltersEqual = (left: number[], right: number[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const areAppliedFiltersEqual = (previous: AppliedFiltersSnapshot, next: AppliedFiltersSnapshot): boolean => {
  return (
    areQueryFiltersEqual(previous.filters, next.filters) &&
    previous.searchText === next.searchText &&
    areActionsFiltersEqual(previous.actionsFilter, next.actionsFilter)
  );
};

interface UseCpsGridSelectionParams {
  gridData: CpsGridDocument[];
  selectedIndexId: number | undefined;
  selectedView: number | undefined;
}

interface UseCpsGridSelectionResult {
  select: SelectDescriptor | undefined;
  onFilterActionsMount: (actions: FilterActionsHandle) => void;
  onSelectionChange: (event: GridSelectionChangeEvent) => void;
  onHeaderSelectionChange: (event: GridHeaderSelectionChangeEvent) => void;
  handleContextMenu: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const useCpsGridSelection = (params: UseCpsGridSelectionParams): UseCpsGridSelectionResult => {
  const { gridData, selectedIndexId, selectedView } = params;
  const { setSelectedDocuments } = useDocumentsContext();
  const { queryRequest } = useSearchContext();
  const [selectionByView, setSelectionByView] = useState<Record<string, SelectDescriptor>>({});
  const [filterActionsHandle, setFilterActionsHandle] = useState<FilterActionsHandle | null>(null);
  const previousAppliedFiltersRef = useRef<AppliedFiltersSnapshot | null>(null);
  const previousPageRef = useRef<PageSnapshot | null>(null);

  const viewKey = `${selectedView ?? 'none'}-${selectedIndexId ?? 'none'}`;
  const select = selectionByView[viewKey] ?? {};

  const clearCurrentSelection = useCallback((): void => {
    setSelectionByView(previousSelection => ({
      ...previousSelection,
      [viewKey]: {}
    }));

    if (setSelectedDocuments) {
      setSelectedDocuments([]);
    }
  }, [setSelectedDocuments, viewKey]);

  useEffect(() => {
    const nextAppliedFilters: AppliedFiltersSnapshot = {
      viewKey,
      filters: queryRequest?.filters ?? [],
      searchText: queryRequest?.searchText ?? '',
      actionsFilter: queryRequest?.actionsFilter ?? []
    };
    const previousAppliedFilters = previousAppliedFiltersRef.current;
    previousAppliedFiltersRef.current = nextAppliedFilters;

    if (
      !previousAppliedFilters ||
      previousAppliedFilters.viewKey !== nextAppliedFilters.viewKey ||
      areAppliedFiltersEqual(previousAppliedFilters, nextAppliedFilters)
    ) {
      return;
    }

    clearCurrentSelection();
  }, [
    queryRequest?.filters,
    queryRequest?.searchText,
    queryRequest?.actionsFilter,
    viewKey,
    clearCurrentSelection
  ]);

  useEffect(() => {
    const nextPage: PageSnapshot = {
      viewKey,
      skip: queryRequest?.skip ?? 0,
      size: queryRequest?.size ?? 0
    };
    const previousPage = previousPageRef.current;
    previousPageRef.current = nextPage;

    if (!previousPage || previousPage.viewKey !== nextPage.viewKey) {
      return;
    }

    if (previousPage.skip === nextPage.skip && previousPage.size === nextPage.size) {
      return;
    }

    clearCurrentSelection();
  }, [queryRequest?.skip, queryRequest?.size, viewKey, clearCurrentSelection]);

  const syncSelectedDocuments = (selection: SelectDescriptor, dataItems: CpsGridDocument[]): void => {
    if (!setSelectedDocuments) {
      return;
    }

    const selectedIds = Object.keys(selection).filter(id => selection[id] === true);
    const selectedItems = dataItems.filter(item => selectedIds.includes(item[DOC_ID_FIELD]));
    setSelectedDocuments(selectedItems);
  };

  const onSelectionChange = (event: GridSelectionChangeEvent): void => {
    const selection = window.getSelection();
    const isShiftClick = event.nativeEvent?.shiftKey;
    if (isShiftClick) {
      selection?.removeAllRanges();
    }
    if (selection && selection.toString().length > 0) {
      return;
    }

    const target = event.nativeEvent?.target as HTMLElement | null;
    if (target?.closest('.linkCell, .linkedCell')) {
      return;
    }

    syncSelectedDocuments(event.select, event.dataItems);
    setSelectionByView(prev => ({
      ...prev,
      [viewKey]: event.select
    }));
  };

  const onHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent): void => {
    syncSelectedDocuments(event.select, event.dataItems);
    setSelectionByView(prev => ({
      ...prev,
      [viewKey]: event.select
    }));
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement;
    const row = target.closest('tr[data-grid-row-index]');

    if (!row || gridData.length === 0) {
      return;
    }

    e.preventDefault();

    const rowIndex = parseInt(row.getAttribute('data-grid-row-index') || '0', 10);
    const clickedDocument = gridData[rowIndex];
    if (!clickedDocument) {
      return;
    }

    const docId = clickedDocument[DOC_ID_FIELD];
    if (!select?.[docId]) {
      const newSelect = { ...select, [docId]: true };
      setSelectionByView(prev => ({
        ...prev,
        [viewKey]: newSelect
      }));
      syncSelectedDocuments(newSelect, gridData);
    }

    const clickedCell = resolveClickedCellMetadata(target, clickedDocument);

    setTimeout(() => {
      filterActionsHandle?.openContextMenu(e.clientX, e.clientY, clickedCell);
    }, 0);
  };

  const onFilterActionsMount = useCallback((actions: FilterActionsHandle): void => {
    setFilterActionsHandle(actions);
  }, []);

  return {
    select,
    onFilterActionsMount,
    onSelectionChange,
    onHeaderSelectionChange,
    handleContextMenu
  };
};
